import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../../lib/api';
import { changerEtatEcriture, comptesTresorerie, detailEcriture, NATURES_AVEC_COMPTE } from '../../lib/argentApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDate, formatDateHeure, formatMontant } from '../../lib/formats';
import { libelle, nomLisible, referenceLisible, texteSur } from '../../lib/libelles';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import { financialKey } from '../FinancialStatement';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { LoadingState } from '../ui/loading-state';
import { SearchableSelect } from '../ui/searchable-select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { toast } from '../ui/sonner';

/**
 * Panneau latéral d'une écriture (livre de caisse, relevés des clients,
 * chauffeurs et fournisseurs): d'où elle vient, à quoi elle est affectée, et
 * les gestes possibles — confirmer, annuler par une écriture liée, rapprocher
 * la trésorerie, affecter un crédit disponible, régulariser un solde
 * historique. Un clic sur une ligne remplace le dépliant « Origine et
 * affectations » répété sur chaque ligne.
 */

// Écritures produites par une pièce ou une reprise: elles s'annulent depuis leur pièce.
const SANS_ANNULATION = ['REVERSAL', 'CUSTOMER_INVOICE', 'CUSTOMER_ADJUSTMENT', 'HISTORICAL_BALANCE', 'HISTORY_RECONCILIATION', 'CASH_ASSIGNMENT'];

const Ligne = ({ titre, children }) => (children == null || children === '' ? null : (
  <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-3 border-b py-1.5 text-sm last:border-0">
    <dt className="text-muted-foreground">{titre}</dt>
    <dd className="min-w-0 break-words">{children}</dd>
  </div>
));

/** Les factures ouvertes du tiers d'une écriture, pour affecter son crédit disponible. */
async function piecesOuvertes(operation) {
  const reponse = await api.get('/finance/statement', { params: { party_type: operation.party_type, party_id: operation.party_id } });
  return (reponse?.data?.data?.documents || [])
    .filter((doc) => !['PAID', 'CANCELLED'].includes(doc.status) && Number(doc.remaining) > 0)
    .map((doc) => ({ value: doc.id, label: referenceLisible(doc.reference, 'Facture sans numéro'), description: `reste ${formatMontant(doc.remaining, doc.currency)}`, reste: Number(doc.remaining) }));
}

export default function DetailEcriture({ operationId, onFermer, onModifie, peutSaisir = true }) {
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [detail, setDetail] = useState(null);
  const [erreur, setErreur] = useState('');
  const [motif, setMotif] = useState('');
  const [compte, setCompte] = useState('');
  const [comptes, setComptes] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [piece, setPiece] = useState('');
  const [montant, setMontant] = useState('');
  const [soldeVerifie, setSoldeVerifie] = useState('');
  const [cle, setCle] = useState(financialKey);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    if (!operationId) return;
    setErreur('');
    try {
      setDetail(await detailEcriture(operationId));
    } catch (e) {
      setErreur(messageErreur(e, 'Le détail de cette écriture est indisponible.'));
    }
  }, [operationId]);

  useEffect(() => {
    setDetail(null);
    setMotif('');
    setCompte('');
    setPiece('');
    setMontant('');
    setSoldeVerifie('');
    setPieces([]);
    setCle(financialKey());
    charger();
  }, [charger]);

  const attenteSansCompte = detail?.status === 'PENDING' && !detail.account_id && NATURES_AVEC_COMPTE.includes(detail.kind) && detail.kind !== 'TRANSFER';
  const aRapprocher = Boolean(detail?.needs_reconciliation);
  const creditLibre = Number(detail?.available) > 0 && ['CUSTOMER', 'SUPPLIER'].includes(detail?.party_type);
  useEffect(() => {
    if (!attenteSansCompte && !aRapprocher) return;
    comptesTresorerie().then(setComptes).catch(() => setComptes([]));
  }, [attenteSansCompte, aRapprocher]);
  useEffect(() => {
    if (!creditLibre || !detail) return;
    piecesOuvertes(detail).then(setPieces).catch(() => setPieces([]));
  }, [creditLibre, detail]);

  const optionsComptes = useMemo(() => comptes.map((row) => ({
    value: row.id,
    label: nomLisible(row.name, 'Compte sans nom'),
    description: libelle('compteTresorerie', row.account_type),
  })), [comptes]);

  const changer = (setter) => (valeur) => {
    setter(valeur);
    setCle(financialKey());
  };

  const agir = async (geste, succes) => {
    setEnvoi(true);
    setErreur('');
    try {
      await geste();
      toast.success(succes);
      setMotif('');
      setCle(financialKey());
      await charger();
      onModifie?.();
    } catch (e) {
      setErreur(messageErreur(e, 'Le geste n’a pas pu être enregistré. Réessayez.'));
    } finally {
      setEnvoi(false);
    }
  };

  const devise = detail?.currency || 'XOF';
  const annulable = detail && detail.status === 'CONFIRMED' && !detail.reversed_by && !SANS_ANNULATION.includes(detail.kind);
  const motifOk = motif.trim().length > 0;
  const bloque = envoi || !enLigne;
  const pieceChoisie = pieces.find((p) => p.value === piece);
  const tiers = detail?.party_name ? nomLisible(detail.party_name) : null;
  const comptesTexte = detail && [
    detail.account_name,
    detail.target_account_name ? `vers ${detail.target_account_name}` : null,
  ].filter(Boolean).join(' ');

  return (
    <Sheet open={Boolean(operationId)} onOpenChange={(ouvert) => !ouvert && onFermer?.()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="detail-ecriture">
        <SheetHeader>
          <SheetTitle>{detail ? libelle('financeKind', detail.kind) : 'Écriture'}</SheetTitle>
          <SheetDescription>
            {detail ? [formatDate(detail.occurred_at), formatMontant(detail.amount, devise), tiers].filter(Boolean).join(' · ') : 'Chargement…'}
          </SheetDescription>
        </SheetHeader>
        {erreur && <p role="alert" className="mt-3 text-sm text-destructive">{erreur}</p>}
        {!detail && !erreur && <div className="mt-4"><LoadingState shape="table" rows={5} columns={2} label="Chargement de l’écriture" /></div>}
        {detail && (
          <div className="mt-4 space-y-5">
            <dl>
              <Ligne titre="N°">{detail.reference_number || null}</Ligne>
              <Ligne titre="État">
                {detail.reversed_by ? `Annulée le ${formatDate(detail.reversed_by.occurred_at)}${detail.reversed_by.reference_number ? ` par ${detail.reversed_by.reference_number}` : ''}${detail.reversed_by.reason ? ` · ${detail.reversed_by.reason}` : ''}` : libelle('financeStatut', detail.status)}
              </Ligne>
              <Ligne titre="Montant">{formatMontant(detail.amount, devise)}</Ligne>
              <Ligne titre="Date">{formatDate(detail.occurred_at)}</Ligne>
              <Ligne titre={detail.party_type ? libelle('typeTiers', detail.party_type) : 'Tiers'}>{tiers}</Ligne>
              <Ligne titre={detail.kind === 'TRANSFER' ? 'Comptes' : 'Compte'}>{comptesTexte || (NATURES_AVEC_COMPTE.includes(detail.kind) ? 'Sans compte de trésorerie' : null)}</Ligne>
              <Ligne titre="Référence">{texteSur(detail.reference, '') || null}</Ligne>
              <Ligne titre="Catégorie">{detail.category ? libelle('categorie_frais', detail.category) : null}</Ligne>
              <Ligne titre="Trajet">{detail.trip ? [detail.trip.plate, detail.trip.status ? libelle('tripStatus', detail.trip.status) : null].filter(Boolean).join(' · ') : null}</Ligne>
              <Ligne titre="Annule">{detail.original ? [detail.original.reference_number, `${libelle('financeKind', detail.original.kind)} du ${formatDate(detail.original.occurred_at)}`, formatMontant(detail.original.amount, devise)].filter(Boolean).join(' · ') : null}</Ligne>
              <Ligne titre="Saisi par">{[detail.actor_name, detail.created_at ? formatDateHeure(detail.created_at) : null].filter(Boolean).join(' · ') || null}</Ligne>
              <Ligne titre="Crédit disponible">{Number(detail.available) > 0 ? formatMontant(detail.available, devise) : null}</Ligne>
            </dl>

            {(detail.allocations?.length > 0 || detail.payments?.length > 0) && (
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{detail.allocations?.length ? 'Affectée à' : 'Réglée par'}</h3>
                <table className="mt-1 w-full text-sm">
                  <tbody>
                    {(detail.allocations || []).map((a) => (
                      <tr key={a.id} className="border-b last:border-0">
                        <td className="py-1.5">{referenceLisible(a.reference, 'Pièce')}</td>
                        <td className="py-1.5 text-right tabular-nums">{formatMontant(a.effective_amount, devise)}</td>
                      </tr>
                    ))}
                    {(detail.payments || []).map((p) => (
                      <tr key={p.id} className="border-b last:border-0">
                        <td className="py-1.5">{[libelle('financeKind', p.kind), formatDate(p.occurred_at)].join(' · ')}</td>
                        <td className="py-1.5 text-right tabular-nums">{formatMontant(p.effective_amount, devise)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {peutSaisir && (detail.status === 'PENDING' || annulable || aRapprocher || creditLibre || detail.kind === 'HISTORICAL_BALANCE') && (
              <section className="space-y-3 rounded-lg border p-3" aria-label="Gestes">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Gestes</h3>
                {(detail.status === 'PENDING' || annulable || aRapprocher || detail.kind === 'HISTORICAL_BALANCE') && (
                  <label className="block text-sm">
                    Motif
                    <Input aria-label="Motif" value={motif} onChange={(e) => changer(setMotif)(e.target.value)} className="mt-1" />
                  </label>
                )}
                {(attenteSansCompte || aRapprocher) && (
                  <div className="text-sm">
                    Compte de trésorerie
                    <SearchableSelect aria-label="Compte de trésorerie" className="mt-1" value={compte} onValueChange={changer(setCompte)} options={optionsComptes} placeholder="Choisir la caisse ou la banque" searchPlaceholder="Rechercher un compte…" emptyMessage="Aucun compte" />
                  </div>
                )}
                {detail.kind === 'HISTORICAL_BALANCE' && detail.party_type === 'CUSTOMER' && (
                  <label className="block text-sm">
                    Solde vérifié du client (crédit : montant négatif)
                    <Input aria-label="Solde vérifié" type="number" value={soldeVerifie} onChange={(e) => changer(setSoldeVerifie)(e.target.value)} className="mt-1" />
                  </label>
                )}
                <div className="flex flex-wrap gap-2">
                  {detail.status === 'PENDING' && (
                    <Button type="button" size="sm" disabled={bloque || !motifOk || (attenteSansCompte && !compte)} onClick={() => agir(() => changerEtatEcriture(detail.id, { action: 'CONFIRM', motif, cle, compte: attenteSansCompte ? compte : undefined }), 'Écriture confirmée.')}>
                      Confirmer
                    </Button>
                  )}
                  {aRapprocher && (
                    <Button type="button" size="sm" variant="outline" disabled={bloque || !motifOk || !compte} onClick={() => agir(() => api.post(`/finance/operations/${detail.id}/reconcile-cash`, { account_id: compte, reason: motif, idempotency_key: cle }), 'Trésorerie rapprochée.')}>
                      Rapprocher la trésorerie
                    </Button>
                  )}
                  {detail.kind === 'HISTORICAL_BALANCE' && detail.party_type === 'CUSTOMER' && (
                    <Button type="button" size="sm" variant="outline" disabled={bloque || !motifOk || soldeVerifie === ''} onClick={() => agir(() => api.post(`/finance/clients/${detail.party_id}/reconcile-history`, { corrected_balance: Number(soldeVerifie), reason: motif, idempotency_key: cle }), 'Solde rapproché.')}>
                      Confirmer le solde rapproché
                    </Button>
                  )}
                  {annulable && (
                    <Button type="button" size="sm" variant="outline" disabled={bloque || !motifOk} onClick={() => agir(() => changerEtatEcriture(detail.id, { action: 'REVERSE', motif, cle }), 'Écriture annulée par une écriture liée.')}>
                      Annuler par une écriture liée
                    </Button>
                  )}
                </div>
                {creditLibre && pieces.length > 0 && (
                  <div className="space-y-2 border-t pt-3">
                    <p className="text-sm font-medium">Affecter le crédit disponible</p>
                    <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_8rem]">
                      <SearchableSelect aria-label="Facture" value={piece} onValueChange={(valeur) => {
                        changer(setPiece)(valeur);
                        const choisie = pieces.find((p) => p.value === valeur);
                        if (choisie && !montant) setMontant(String(Math.min(choisie.reste, Number(detail.available))));
                      }} options={pieces} placeholder="Choisir une facture" searchPlaceholder="Rechercher une facture…" emptyMessage="Aucune facture ouverte" />
                      <Input aria-label="Montant affecté" type="number" min="1" value={montant} onChange={(e) => changer(setMontant)(e.target.value)} />
                    </div>
                    <Button type="button" size="sm" variant="outline" disabled={bloque || !pieceChoisie || !(Number(montant) > 0)} onClick={() => agir(() => api.post(`/finance/operations/${detail.id}/allocations`, { allocations: [{ document_id: piece, amount: Number(montant) }], idempotency_key: cle }), 'Crédit affecté.')}>
                      Affecter
                    </Button>
                  </div>
                )}
                {!enLigne && <p className="text-xs text-muted-foreground">Réseau nécessaire.</p>}
              </section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
